import { motion } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";

const ROWS = [
  ["Focus", "Volume & projection", "Lip quality, hydration & condition"],
  ["Philosophy", "Change the shape", "Enhance what is naturally yours"],
  ["Look", "A visible 'filler look'", "Soft, fresh, healthy-looking lips"],
  ["Approach", "Traditional dermal filler", "Lip hydration & revitalisation concept"],
];

const Statement = ({ lines, outline, align = "left" }) => (
  <motion.h2
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.2 }}
    variants={{ show: { transition: { staggerChildren: 0.12 } } }}
    className={`font-serif font-light leading-[0.92] tracking-tight text-[17vw] sm:text-[8.5rem] lg:text-[9.5rem] ${
      outline ? "text-outline-pearl" : "italic text-[#E893AE]"
    } ${align === "right" ? "lg:text-right" : ""}`}
  >
    {lines.map((l) => (
      <span key={l} className="block overflow-hidden">
        <motion.span
          variants={{
            hidden: { y: "110%" },
            show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
          }}
          className="block"
        >
          {l}
        </motion.span>
      </span>
    ))}
  </motion.h2>
);

const Manifesto = () => (
  <section id="booster" data-testid="manifesto-section" className="relative bg-[#1C1417] py-28 sm:py-40 overflow-hidden">
    <div className="absolute -top-40 -right-40 w-[540px] h-[540px] rounded-full bg-[#A31D4F]/25 blur-[150px] pointer-events-none" />
    <div className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-[#D84A75]/10 blur-[130px] pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <Eyebrow light>The philosophy</Eyebrow>
      </Reveal>

      <div className="relative mt-10 grid lg:grid-cols-2 gap-8 lg:gap-4 items-center">
        <div data-testid="manifesto-statement-text">
          <Statement lines={["NOT", "A", "FILLER."]} outline />
        </div>
        <Statement lines={["A", "LIP", "BOOSTER."]} align="right" />

        <motion.div
          initial={{ opacity: 0, y: 60, rotate: -8 }}
          whileInView={{ opacity: 1, y: 0, rotate: -5 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-[250px]"
        >
          <div className="absolute -inset-6 bg-[#A31D4F]/35 blur-3xl rounded-full" />
          <img
            src="/assets/hero-poster.webp"
            alt="Skinfill Bacio syringe and packaging"
            loading="lazy"
            className="relative rounded-3xl border border-white/10 shadow-[0_50px_100px_-30px_rgba(0,0,0,0.8)] aspect-[9/16] object-cover"
          />
        </motion.div>
      </div>

      <Reveal delay={0.15}>
        <div className="lg:hidden mt-10 mx-auto w-[210px] -rotate-3">
          <img
            src="/assets/hero-poster.webp"
            alt="Skinfill Bacio syringe and packaging"
            loading="lazy"
            className="rounded-3xl border border-white/10 shadow-2xl aspect-[9/16] object-cover"
          />
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <p className="mt-14 lg:mt-16 max-w-xl text-[#F4EBE6]/70 text-base sm:text-lg font-light leading-relaxed">
          Created for people who want to focus on the quality, hydration and revitalised appearance of their lips —
          without making exaggerated volume the objective.
        </p>
      </Reveal>

      <Reveal delay={0.25} className="mt-20 sm:mt-28">
        <div data-testid="comparison-table-matrix" className="rounded-3xl overflow-hidden border border-white/10">
          <div className="grid grid-cols-3 bg-white/5">
            <div className="p-4 sm:p-6" />
            <div className="p-4 sm:p-6 border-l border-white/10">
              <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[#F4EBE6]/50">
                Traditional lip filler
              </p>
            </div>
            <div className="p-4 sm:p-6 border-l border-[#D84A75]/40 bg-[#A31D4F]/15">
              <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[#E893AE]">
                SKINFILL BACIO®
              </p>
            </div>
          </div>
          {ROWS.map(([label, filler, bacio], i) => (
            <div key={label} className={`grid grid-cols-3 ${i % 2 ? "bg-white/[0.03]" : ""} border-t border-white/10`}>
              <div className="p-4 sm:p-6">
                <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[#F4EBE6]/60">{label}</p>
              </div>
              <div className="p-4 sm:p-6 border-l border-white/10">
                <p className="text-xs sm:text-sm text-[#F4EBE6]/55 font-light">{filler}</p>
              </div>
              <div className="p-4 sm:p-6 border-l border-[#D84A75]/40 bg-[#A31D4F]/10">
                <p className="text-xs sm:text-sm text-[#FAF7F5] font-light">{bacio}</p>
              </div>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export default Manifesto;
